import React from "react";
import { graphql, useFragment } from "react-relay";

import { type SwitchProps as RadixProps, Root, Thumb } from "@radix-ui/react-switch";
import cx from "classnames";

import ThemeContext from "~/common/ThemeContext";
import { useMutationPromise } from "~/lib/relayUtil";
import { CalendarObserver } from "~/nav/panels/Meetings";
import { toast, Tooltip } from "~/ui";

import { TIME_BLOCKS_TO_EXCLUDE } from "../../calendar/utils/types";
import { AppContext } from "../../common";
import { useMeetingRecorderFeatureFlagOn, useSchedulingMeetingAssistantFlagOn } from "../featureFlag";
import { anyConferenceCallSupported } from "../MeetingRecordingBanner";
import { ReactComponent as RecordIconCheckedDarkSVG } from "./icon-checked-dark.svg";
import { ReactComponent as RecordIconCheckedLightSVG } from "./icon-checked-light.svg";
import { ReactComponent as RecordIconUncheckedDarkSVG } from "./icon-unchecked-dark.svg";
import { ReactComponent as RecordIconUncheckedLightSVG } from "./icon-unchecked-light.svg";

import type { RecordIcon_event$data, RecordIcon_event$key } from "~/__generated__/RecordIcon_event.graphql";
import type { RecordIcon_ToggleMutation as ToggleMutation } from "~/__generated__/RecordIcon_ToggleMutation.graphql";

import styles from "./RecordIcon.module.css";

type RecordIconEvent = Omit<RecordIcon_event$data, " $fragmentType">;

const eventFragment = graphql`
	fragment RecordIcon_event on Event {
		id
		guid
		willBeRecorded
		start
		status
		isAllDay
		timeBlockType
		conferenceCalls {
			provider
			url
		}
		calendar {
			id
		}
	}
`;

// ===========
//  Functions
// ===========

export function useChangeWillBeRecordedMutation() {
	const [commit, isInFlight] = useMutationPromise<ToggleMutation>(graphql`
		mutation RecordIcon_ToggleMutation($eventGuid: String!, $value: Boolean!) {
			toggleEventSchedulingMeetingAssistantAutomation(eventGuid: $eventGuid, value: $value) {
				event {
					id
					guid
					willBeRecorded
					stream {
						id
						schedulingMeetingAssistantAutomationIsActive
					}
				}
			}
		}
	`);

	const changeWillBeRecorded = React.useCallback(
		async (calendarId: string, eventGuid: string, value: boolean) => {
			try {
				await commit({
					variables: { eventGuid, value },
					optimisticUpdater(store) {
						CalendarObserver.getInstance().updateCalendarEvent(
							calendarId,
							eventGuid,
							{ willBeRecorded: value ? "YES" : "NO" },
							store,
						);
					},
				});
			} catch (e) {
				toast.error("Something went wrong, please try again later", { title: "Failed to save your settings" });

				// Reverting the optimistic update we made above
				CalendarObserver.getInstance().updateCalendarEvent(calendarId, eventGuid, {
					willBeRecorded: value ? "NO" : "YES",
				});
			}
		},
		[commit],
	);

	return [changeWillBeRecorded, isInFlight] as const;
}

export function useRecordIconShouldBeVisible(eventKey: RecordIcon_event$key | null) {
	const userHasAccessToScheduledMeetingRecordings = useUserHasAccessToScheduledMeetingRecordings();
	const event = useFragment(eventFragment, eventKey);

	// We do some cheap checks: these checks are similar to the ones done in the backend.
	// See: auto_record.py#_calendar_event_will_be_recorded()
	const shouldRender = userHasAccessToScheduledMeetingRecordings && event && isEventValid(event);

	if (!shouldRender) return false;

	return Boolean(shouldRender && event.willBeRecorded === "YES");
}

export function isEventValid(event: RecordIconEvent) {
	return Boolean(
		event.status !== "cancelled" &&
			// Same as event_is_valid in auto_record.py#_calendar_event_will_be_recorded():
			event.start &&
			new Date(event.start).getTime() >= Date.now() &&
			!event.isAllDay &&
			!TIME_BLOCKS_TO_EXCLUDE.includes(event.timeBlockType || "") &&
			anyConferenceCallSupported(event.conferenceCalls),
	);
}

export function useUserHasAccessToScheduledMeetingRecordings() {
	const { account } = React.useContext(AppContext);

	const schedulingMeetingAssistantFlagOn = useSchedulingMeetingAssistantFlagOn();
	const meetingRecorderFeatureFlagOn = useMeetingRecorderFeatureFlagOn();

	return Boolean(
		// Same as task_is_active in auto_record.py#_calendar_event_will_be_recorded():
		// - feature_flag_enabled
		schedulingMeetingAssistantFlagOn &&
			// - copilot_enabled
			account?.allowAiFeatures &&
			account?.allowMeetingRecording &&
			meetingRecorderFeatureFlagOn,
	);
}

// ===========
//   Switch
// ===========

/**
 * A switch we use for auto-record rules.
 * @param rootElement - Use `div` if this switch is inside a `button` to prevent invalid DOM nesting.
 * Otherwise use `button`.
 */
export function RecordIconSwitch({
	tooltipContent,
	className,
	rootElement: SwitchRootElement = "button",
	...rest
}: RadixProps & { rootElement?: "button" | "div"; tooltipContent?: string }) {
	const element = (
		<div className={cx(styles.wrapper, styles.toggleWrapper, className)}>
			<Root asChild className={styles.switch} {...rest}>
				<SwitchRootElement>
					<Thumb className={styles.thumb} />
				</SwitchRootElement>
			</Root>
		</div>
	);

	if (tooltipContent) {
		return (
			<Tooltip content={tooltipContent} className={styles.tooltip} maxWidth="10.15em">
				{element}
			</Tooltip>
		);
	}

	return element;
}

type SwitchProps = Omit<RadixProps, "asChild" | "checked" | "onCheckedChange">;

export function RecordIconSwitchEventWrapper({
	event: eventKey,
	...rest
}: SwitchProps & { event: RecordIcon_event$key }) {
	const event = useFragment(eventFragment, eventKey);

	return <RecordIconSwitchEvent event={event} {...rest} />;
}

export function RecordIconSwitchEvent({ event, className, ...rest }: SwitchProps & { event: RecordIconEvent }) {
	const [changeWillBeRecorded, mutationInProgress] = useChangeWillBeRecordedMutation();

	// We do some cheap checks: these checks are similar to the ones done in the backend.
	// See: auto_record.py#_calendar_event_will_be_recorded()
	const userHasAccessToScheduledMeetingRecordings = useUserHasAccessToScheduledMeetingRecordings();
	const futureEvent = event.start && new Date(event.start).getTime() >= Date.now();
	const shouldRender = userHasAccessToScheduledMeetingRecordings && futureEvent;

	const eventIsValid = isEventValid(event);

	const onCheckedChange = React.useCallback(
		async (checked: boolean) => {
			if (mutationInProgress || !eventIsValid) return;

			await changeWillBeRecorded(event.calendar.id, event.guid, checked);
		},
		[event.calendar.id, event.guid, changeWillBeRecorded, mutationInProgress, eventIsValid],
	);

	if (!shouldRender) {
		return null;
	}

	if (!eventIsValid) {
		return (
			<RecordIconSwitch
				{...rest}
				checked={false}
				onCheckedChange={undefined}
				tooltipContent={
					event.conferenceCalls.length === 0
						? "Recording this event requires a video call"
						: "This event cannot be recorded"
				}
				className={className}
				disabled
			/>
		);
	}

	if (event.willBeRecorded === "UNKNOWN") {
		// we might be waiting for a WS message with a new value
		return <div className={styles.wrapper} />;
	} else if (event.willBeRecorded === "RESTRICTED") {
		return (
			<RecordRestrictedIcon
				className={className}
				tooltipContent="Recording this meeting is restricted by your workspace"
			/>
		);
	}

	const checked = event.willBeRecorded === "YES";

	return (
		<RecordIconSwitch
			{...rest}
			checked={checked}
			onCheckedChange={onCheckedChange}
			tooltipContent={checked ? "This meeting will be recorded" : "Enable Auto-record"}
			className={className}
		/>
	);
}

// ===========
//    Icon
// ===========
function RecordRestrictedIcon({ className, tooltipContent }: { className?: string; tooltipContent?: string }) {
	const element = <span className={cx(styles.wrapper, styles.restrictedIcon, className)} data-state="restricted" />;

	if (tooltipContent) {
		return (
			<Tooltip content={tooltipContent} className={styles.tooltip} maxWidth="14.25em">
				{element}
			</Tooltip>
		);
	}

	return element;
}

export function RecordIconIndicator({ checked, className }: { checked?: boolean; className?: string }) {
	if (checked) {
		return <span className={cx(styles.wrapper, styles.icon, className, styles.enabled)} />;
	}

	// won't be recorded - we still want the space to be rendered in case the value changes. Possible values:
	// - UNKNOWN: we might be waiting for a WS message with a new value
	// - NO | RESTRICTED: the event will not be recorded, so we don't want to show the icon
	return <span className={cx(styles.wrapper, styles.icon, className)} />;
}

function RecordIcon({ checked, className }: { checked?: boolean; className?: string }) {
	const { isDarkMode } = React.useContext(ThemeContext);

	if (checked) {
		return isDarkMode ? (
			<RecordIconCheckedDarkSVG className={className} />
		) : (
			<RecordIconCheckedLightSVG className={className} />
		);
	}

	return isDarkMode ? (
		<RecordIconUncheckedDarkSVG className={className} />
	) : (
		<RecordIconUncheckedLightSVG className={className} />
	);
}

export default RecordIcon;
